import { type RefObject, useEffect, useMemo, useRef, useState } from "react";
import {
  applyDensity,
  applyMode,
  Density,
  Mode
} from "@cloudscape-design/global-styles";
import AppLayout from "@cloudscape-design/components/app-layout";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import BreadcrumbGroup from "@cloudscape-design/components/breadcrumb-group";
import Button from "@cloudscape-design/components/button";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import Container from "@cloudscape-design/components/container";
import ContentLayout from "@cloudscape-design/components/content-layout";
import Flashbar from "@cloudscape-design/components/flashbar";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import HelpPanel from "@cloudscape-design/components/help-panel";
import Input from "@cloudscape-design/components/input";
import Link from "@cloudscape-design/components/link";
import Multiselect from "@cloudscape-design/components/multiselect";
import Popover from "@cloudscape-design/components/popover";
import Select from "@cloudscape-design/components/select";
import SegmentedControl from "@cloudscape-design/components/segmented-control";
import SideNavigation from "@cloudscape-design/components/side-navigation";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { io } from "socket.io-client";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {
  clearFeedHistory,
  apiOrigin,
  createFeed,
  createLocation,
  deleteLocation,
  deleteFeed,
  deleteTenant,
  getProfileNameAvailability,
  getCurrentSession,
  getAdminTelemetry,
  getAdminLocations,
  getAdminTenants,
  getDashboardSummary,
  getDevices,
  getFeeds,
  getHealth,
  googleClientId,
  isProductionBuild,
  provisionTenant,
  signInForLocalDevelopment,
  signInWithGoogle,
  signOut,
  suspendTenant,
  updateFeed,
  updateDeviceLocation,
  updateLocation,
  updateUserProfile,
  updateTenant,
  updateUserPreferences,
  type AdminLocation,
  type AdminTelemetry,
  type AuthSession,
  type DashboardSummary,
  type Device,
  type Feed,
  type HealthResponse,
  type NameAvailability,
  type ProfileNameAvailability,
  type TelemetryPoint,
  type Tenant,
  type UserPreferences
} from "./api";

type VisualMode = "light" | "dark";
type ContentDensity = "comfortable" | "compact";
type Route =
  | "/platform"
  | "/live"
  | "/admin"
  | "/locations"
  | "/feeds"
  | "/devices"
  | "/settings"
  | "/help"
  | "/account";
type LiveScope = "global" | "tenant" | "device";
type LiveChartPoint = {
  time: string;
  epochMs: number;
  [feedKey: string]: number | string | null;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (options: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              shape?: "rectangular" | "pill" | "circle" | "square";
              width?: number;
            }
          ) => void;
        };
      };
    };
  }
}

const defaultUserPreferences: UserPreferences = {
  mode: "dark",
  density: "comfortable",
  defaultDashboard: "platform",
  telemetryRefreshSeconds: 5,
  realtimeNotice: true
};

const liveLineColors = [
  "#0972d3",
  "#2ea597",
  "#f89256",
  "#d13212",
  "#7d2ae8",
  "#1f77b4",
  "#17becf",
  "#bcbd22",
  "#ff7f0e",
  "#8c564b",
  "#e377c2",
  "#9467bd"
];
const liveRefreshMinIntervalMs = 250;
const liveAllDevicesOptionValue = "__all_devices__";

function buildDefaultFeed(tenantId = ""): Feed {
  return {
    key: "",
    label: "",
    unit: "",
    kind: "line",
    tenantId
  };
}

const feedKindOptions = [
  { label: "Line", value: "line" },
  { label: "Bar", value: "bar" },
  { label: "Gauge", value: "gauge" }
];

const liveSpeedOptions = [
  { label: "Realtime (as received)", value: "realtime" },
  { label: "1s", value: "1000" },
  { label: "5s", value: "5000" },
  { label: "10s", value: "10000" },
  { label: "1m", value: "60000" },
  { label: "5m", value: "300000" },
  { label: "10m", value: "600000" },
  { label: "30m", value: "1800000" },
  { label: "1hr", value: "3600000" },
  { label: "2hr", value: "7200000" },
  { label: "6hr", value: "21600000" },
  { label: "12hr", value: "43200000" },
  { label: "24hr", value: "86400000" }
];

const pageMeta: Record<Route, { title: string; description: string; breadcrumb: string }> = {
  "/platform": {
    title: "Tenant Overview",
    description: "Realtime telemetry overview for the current tenant.",
    breadcrumb: "Tenant overview"
  },
  "/live": {
    title: "Live",
    description: "Live feed graphs with realtime or scheduled updates and stale-value zero fallback.",
    breadcrumb: "Live"
  },
  "/admin": {
    title: "Global Admin",
    description: "Platform-wide telemetry, tenant management, and throttling.",
    breadcrumb: "Global admin"
  },
  "/locations": {
    title: "Locations",
    description: "Building, floor, and room hierarchy with live telemetry counts.",
    breadcrumb: "Locations"
  },
  "/feeds": {
    title: "Feeds",
    description: "Create feeds, inspect feed activity, and clear stream history.",
    breadcrumb: "Feeds"
  },
  "/devices": {
    title: "Devices",
    description: "Registered hardware profiles and telemetry activity by device.",
    breadcrumb: "Devices"
  },
  "/settings": {
    title: "User Settings",
    description: "Personal dashboard preferences for theme, density, and telemetry refresh behavior.",
    breadcrumb: "User settings"
  },
  "/help": {
    title: "Help",
    description: "Account, device onboarding, and platform usage support.",
    breadcrumb: "Help"
  },
  "/account": {
    title: "Account",
    description: "Signed-in user, provider, and tenant workspace details.",
    breadcrumb: "Account"
  }
};

function parseRouteFromHash(hash: string): Route {
  const path = hash.startsWith("#") ? hash.slice(1) : hash;
  if (
    path === "/live" ||
    path === "/admin" ||
    path === "/locations" ||
    path === "/feeds" ||
    path === "/devices" ||
    path === "/settings" ||
    path === "/help" ||
    path === "/account"
  ) {
    return path;
  }
  return "/platform";
}

function routeToHref(route: Route) {
  return `#${route}`;
}

function resolveVisualMode(preference: UserPreferences["mode"]): VisualMode {
  if (preference === "system") {
    return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }

  return preference;
}

function formatTimestamp(timestamp: string | null | undefined) {
  if (!timestamp) {
    return "-";
  }

  return new Date(timestamp).toLocaleString();
}

function formatAge(timestamp: string | null, nowMs: number) {
  if (!timestamp) {
    return "No data yet";
  }

  const ageSeconds = Math.max(0, Math.floor((nowMs - new Date(timestamp).getTime()) / 1000));
  if (ageSeconds < 60) {
    return `${ageSeconds}s ago`;
  }

  const minutes = Math.floor(ageSeconds / 60);
  const seconds = ageSeconds % 60;
  return `${minutes}m ${seconds}s ago`;
}

function telemetryPointKey(point: TelemetryPoint) {
  return [
    point.time,
    point.tenantId,
    point.locationId,
    point.deviceId,
    point.feedKey,
    point.source
  ].join("|");
}

function mergeTelemetry(current: TelemetryPoint[], incoming: TelemetryPoint[], limit = 240) {
  const merged = [...incoming, ...current];
  const deduped: TelemetryPoint[] = [];
  const seen = new Set<string>();

  for (const point of merged) {
    const key = telemetryPointKey(point);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    deduped.push(point);

    if (deduped.length >= limit) {
      break;
    }
  }

  return deduped;
}

export default function App() {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mode, setMode] = useState<VisualMode>("dark");
  const [density, setDensity] = useState<ContentDensity>("comfortable");
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [preferenceDraft, setPreferenceDraft] =
    useState<UserPreferences>(defaultUserPreferences);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [profileDraft, setProfileDraft] = useState({ name: "", tenantName: "" });
  const [profileAvailability, setProfileAvailability] =
    useState<ProfileNameAvailability | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [activeRoute, setActiveRoute] = useState<Route>(() =>
    parseRouteFromHash(window.location.hash)
  );
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [adminTelemetry, setAdminTelemetry] = useState<AdminTelemetry | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [adminLocations, setAdminLocations] = useState<AdminLocation[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [telemetry, setTelemetry] = useState<TelemetryPoint[]>([]);
  const [socketConnected, setSocketConnected] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showRealtimeNotice, setShowRealtimeNotice] = useState(true);
  const [feedDraft, setFeedDraft] = useState<Feed>(buildDefaultFeed());
  const [editingFeedKey, setEditingFeedKey] = useState<string | null>(null);
  const [savingFeed, setSavingFeed] = useState(false);
  const [liveSeries, setLiveSeries] = useState<LiveChartPoint[]>([]);
  const [liveDeviceSeries, setLiveDeviceSeries] = useState<LiveChartPoint[]>([]);
  const [liveClockMs, setLiveClockMs] = useState(() => Date.now());
  const [uiNowMs, setUiNowMs] = useState(() => Date.now());
  const [runtimeUptimeSeconds, setRuntimeUptimeSeconds] = useState(0);
  const [liveScope, setLiveScope] = useState<LiveScope>("global");
  const [liveTenantId, setLiveTenantId] = useState<string | null>(null);
  const [liveDeviceId, setLiveDeviceId] = useState<string | null>(null);
  const [liveSpeedValue, setLiveSpeedValue] = useState("realtime");
  const [liveDeviceFeedKey, setLiveDeviceFeedKey] = useState<string | null>(null);

  const latestNumericByFeedRef = useRef<Record<string, number | null>>({});
  const latestTimestampByFeedRef = useRef<Record<string, string | null>>({});
  const latestNumericByDeviceFeedRef = useRef<Record<string, number | null>>({});
  const latestTimestampByDeviceFeedRef = useRef<Record<string, string | null>>({});
  const lastLiveRenderMsRef = useRef(0);
  const lastLiveDeviceRenderMsRef = useRef(0);
  const snapshotInFlightRef = useRef(false);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);
  const googleButtonRenderedRef = useRef(false);

  const tenantFilterOptions = useMemo(() => {
    const tenantNameById = new Map<string, string>();
    for (const tenant of tenants) {
      tenantNameById.set(tenant.id, tenant.name);
    }
    if (summary?.tenant) {
      tenantNameById.set(summary.tenant.id, summary.tenant.name);
    }

    const tenantIds = new Set<string>();
    for (const point of telemetry) {
      tenantIds.add(point.tenantId);
    }
    for (const tenantId of tenantNameById.keys()) {
      tenantIds.add(tenantId);
    }

    return Array.from(tenantIds).map((tenantId) => ({
      label: tenantNameById.get(tenantId) ?? tenantId,
      value: tenantId
    }));
  }, [summary?.tenant, telemetry, tenants]);

  const deviceFilterOptions = useMemo(() => {
    if (devices.length > 0) {
      return devices
        .map((device) => ({
          label: device.hardwareId,
          value: device.id
        }))
        .sort((a, b) => {
          const labelCompare = a.label.localeCompare(b.label);
          if (labelCompare !== 0) {
            return labelCompare;
          }
          return a.value.localeCompare(b.value);
        });
    }

    const deviceIds = new Set<string>();
    for (const point of telemetry) {
      deviceIds.add(point.deviceId);
    }

    return Array.from(deviceIds)
      .map((deviceId) => ({
        label: deviceId,
        value: deviceId
      }))
      .sort((a, b) => {
        const labelCompare = a.label.localeCompare(b.label);
        if (labelCompare !== 0) {
          return labelCompare;
        }
        return a.value.localeCompare(b.value);
      });
  }, [devices, telemetry]);

  const deviceScopeOptions = useMemo(() => {
    if (deviceFilterOptions.length === 0) {
      return deviceFilterOptions;
    }

    return [
      { label: `All devices (${deviceFilterOptions.length})`, value: liveAllDevicesOptionValue },
      ...deviceFilterOptions
    ];
  }, [deviceFilterOptions]);

  const liveFeedOptions = useMemo(
    () =>
      (summary?.feeds ?? []).map((feed) => ({
        label: feed.label,
        value: feed.key
      })),
    [summary?.feeds]
  );

  const resolvedLiveTenantId = useMemo(() => {
    if (liveTenantId && tenantFilterOptions.some((option) => option.value === liveTenantId)) {
      return liveTenantId;
    }
    if (
      summary?.tenant.id &&
      tenantFilterOptions.some((option) => option.value === summary.tenant.id)
    ) {
      return summary.tenant.id;
    }
    return tenantFilterOptions[0]?.value ?? null;
  }, [liveTenantId, summary?.tenant.id, tenantFilterOptions]);

  const resolvedLiveDeviceId = useMemo(() => {
    if (liveDeviceId && deviceScopeOptions.some((option) => option.value === liveDeviceId)) {
      return liveDeviceId;
    }
    if (deviceScopeOptions.some((option) => option.value === liveAllDevicesOptionValue)) {
      return liveAllDevicesOptionValue;
    }
    return deviceScopeOptions[0]?.value ?? null;
  }, [deviceScopeOptions, liveDeviceId]);

  const resolvedLiveDeviceFeedKey = useMemo(() => {
    if (liveDeviceFeedKey && liveFeedOptions.some((option) => option.value === liveDeviceFeedKey)) {
      return liveDeviceFeedKey;
    }
    const temperatureFeed = liveFeedOptions.find((option) => option.value === "temperature");
    if (temperatureFeed) {
      return temperatureFeed.value;
    }
    return liveFeedOptions[0]?.value ?? null;
  }, [liveDeviceFeedKey, liveFeedOptions]);

  const selectedTenantOption =
    tenantFilterOptions.find((option) => option.value === resolvedLiveTenantId) ?? null;
  const selectedDeviceOption =
    deviceScopeOptions.find((option) => option.value === resolvedLiveDeviceId) ?? null;
  const selectedLiveDeviceFeedOption =
    liveFeedOptions.find((option) => option.value === resolvedLiveDeviceFeedKey) ?? null;
  const selectedLiveSpeedOption =
    liveSpeedOptions.find((option) => option.value === liveSpeedValue) ?? liveSpeedOptions[0];
  const liveSpeedMs =
    selectedLiveSpeedOption.value === "realtime" ? 1000 : Number(selectedLiveSpeedOption.value);
  const staleAfterMs = Math.max(5000, liveSpeedMs * 2);
  const isGlobalAdmin = authSession?.user.role === "global-admin";
  const currentTenantId = authSession?.user.tenantId ?? summary?.tenant.id ?? "";

  const liveTelemetry = useMemo(() => {
    if (liveScope === "tenant") {
      if (!resolvedLiveTenantId) {
        return [];
      }
      return telemetry.filter((point) => point.tenantId === resolvedLiveTenantId);
    }

    if (liveScope === "device") {
      if (!resolvedLiveDeviceId) {
        return [];
      }
      if (resolvedLiveDeviceId === liveAllDevicesOptionValue) {
        return telemetry;
      }
      return telemetry.filter((point) => point.deviceId === resolvedLiveDeviceId);
    }

    return telemetry;
  }, [liveScope, resolvedLiveDeviceId, resolvedLiveTenantId, telemetry]);

  const liveScopeDescription = useMemo(() => {
    if (liveScope === "tenant") {
      return selectedTenantOption ? `Tenant scope: ${selectedTenantOption.label}` : "Tenant scope";
    }
    if (liveScope === "device") {
      if (resolvedLiveDeviceId === liveAllDevicesOptionValue) {
        return "Device scope: all devices";
      }
      return selectedDeviceOption ? `Device scope: ${selectedDeviceOption.label}` : "Device scope";
    }
    return isGlobalAdmin
      ? "Global scope"
      : `Workspace scope: ${summary?.tenant.name ?? "current tenant"}`;
  }, [
    isGlobalAdmin,
    liveScope,
    resolvedLiveDeviceId,
    selectedDeviceOption,
    selectedTenantOption,
    summary?.tenant.name
  ]);
  const liveScopeKey = `${liveScope}:${resolvedLiveTenantId ?? "none"}:${resolvedLiveDeviceId ?? "none"}`;
  const liveFeedSignature = (summary?.feeds ?? [])
    .map((feed) => `${feed.key}:${feed.kind}`)
    .sort()
    .join("|");
  const liveDeviceIdsForChart = useMemo(() => {
    if (liveScope !== "device") {
      return [];
    }
    if (!resolvedLiveDeviceId) {
      return [];
    }
    if (resolvedLiveDeviceId === liveAllDevicesOptionValue) {
      return deviceFilterOptions.map((option) => option.value);
    }
    return [resolvedLiveDeviceId];
  }, [deviceFilterOptions, liveScope, resolvedLiveDeviceId]);

  const deviceLabelById = useMemo(() => {
    const labelById = new Map<string, string>();
    for (const device of devices) {
      labelById.set(device.id, device.hardwareId);
    }
    return labelById;
  }, [devices]);
  const liveDeviceChartKey = `${liveScopeKey}:${resolvedLiveDeviceFeedKey ?? "none"}:${liveDeviceIdsForChart.join(",")}`;
  const liveScopeOptions = useMemo(
    () => [
      { id: "global", text: isGlobalAdmin ? "Global" : "Workspace" },
      ...(isGlobalAdmin ? [{ id: "tenant", text: "Tenant" }] : []),
      { id: "device", text: "Device" }
    ],
    [isGlobalAdmin]
  );

  useEffect(() => {
    if (!isGlobalAdmin && liveScope === "tenant") {
      setLiveScope("global");
    }
  }, [isGlobalAdmin, liveScope]);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    const applySession = (session: AuthSession) => {
      setAuthSession(session);
      setPreferenceDraft(session.user.preferences);
      setProfileDraft({ name: session.user.name, tenantName: session.tenant.name });
      setProfileAvailability(null);
      setMode(resolveVisualMode(session.user.preferences.mode));
      setDensity(session.user.preferences.density);
      setShowRealtimeNotice(session.user.preferences.realtimeNotice);
    };

    const restoreSession = async () => {
      try {
        const session = await getCurrentSession(controller.signal);
        if (disposed) {
          return;
        }

        if (session) {
          applySession(session);
          return;
        }

        if (!isProductionBuild && !googleClientId) {
          const devSession = await signInForLocalDevelopment();
          if (disposed) {
            return;
          }
          applySession(devSession);
          const dashboard =
            devSession.user.role === "global-admin"
              ? devSession.user.preferences.defaultDashboard
              : devSession.user.preferences.defaultDashboard === "admin"
                ? "platform"
                : devSession.user.preferences.defaultDashboard;
          window.location.hash = routeToHref(
            `/${dashboard}` as Route
          );
          return;
        }
      } catch (error: unknown) {
        if (disposed) {
          return;
        }

        if (error instanceof DOMException && error.name === "AbortError") {
          setAuthError("Session restore timed out. Please sign in again.");
          return;
        }

        setAuthError(error instanceof Error ? error.message : "Unable to restore session.");
      } finally {
        if (!disposed) {
          setAuthLoading(false);
        }
      }
    };

    void restoreSession();

    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (authLoading || authSession || !googleClientId || googleButtonRenderedRef.current) {
      return;
    }

    const renderGoogleButton = () => {
      if (!window.google || !googleButtonRef.current || googleButtonRenderedRef.current) {
        return;
      }

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: ({ credential }) => {
          if (!credential) {
            setAuthError("Google did not return a sign-in credential.");
            return;
          }

          setAuthError(null);
          setAuthLoading(true);
          signInWithGoogle(credential)
            .then((session) => {
              setAuthSession(session);
              setPreferenceDraft(session.user.preferences);
              setProfileDraft({ name: session.user.name, tenantName: session.tenant.name });
              setProfileAvailability(null);
              setMode(resolveVisualMode(session.user.preferences.mode));
              setDensity(session.user.preferences.density);
              setShowRealtimeNotice(session.user.preferences.realtimeNotice);
              const dashboard =
                session.user.role === "global-admin"
                  ? session.user.preferences.defaultDashboard
                  : session.user.preferences.defaultDashboard === "admin"
                    ? "platform"
                    : session.user.preferences.defaultDashboard;
              window.location.hash = routeToHref(
                `/${dashboard}` as Route
              );
            })
            .catch((error: unknown) => {
              setAuthError(error instanceof Error ? error.message : "Google sign-in failed.");
            })
            .finally(() => setAuthLoading(false));
        }
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: mode === "dark" ? "filled_black" : "outline",
        size: "large",
        shape: "pill",
        width: 280
      });
      googleButtonRenderedRef.current = true;
    };

    if (window.google) {
      renderGoogleButton();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = renderGoogleButton;
    script.onerror = () => setAuthError("Unable to load Google sign-in.");
    document.head.appendChild(script);
  }, [authLoading, authSession, mode]);

  useEffect(() => {
    applyMode(mode === "dark" ? Mode.Dark : Mode.Light);
  }, [mode]);

  useEffect(() => {
    applyDensity(density === "compact" ? Density.Compact : Density.Comfortable);
  }, [density]);

  useEffect(() => {
    const onHashChange = () => {
      setActiveRoute(parseRouteFromHash(window.location.hash));
    };

    window.addEventListener("hashchange", onHashChange);
    if (!window.location.hash) {
      window.location.hash = routeToHref("/platform");
    }

    return () => {
      window.removeEventListener("hashchange", onHashChange);
    };
  }, []);

  useEffect(() => {
    if (authSession && !isGlobalAdmin && activeRoute === "/admin") {
      window.location.hash = routeToHref("/platform");
    }
  }, [activeRoute, authSession, isGlobalAdmin]);

  useEffect(() => {
    if (!authSession) {
      return;
    }

    let disposed = false;

    const syncSnapshot = async ({
      includeTenants
    }: {
      includeTenants: boolean;
    }) => {
      if (snapshotInFlightRef.current) {
        return;
      }

      snapshotInFlightRef.current = true;
      const controller = new AbortController();

      try {
        const [healthResponse, dashboardResponse, deviceResponse, feedResponse] = await Promise.all([
          getHealth(controller.signal),
          getDashboardSummary(controller.signal),
          getDevices(controller.signal),
          getFeeds(controller.signal)
        ]);
        const adminResponse = isGlobalAdmin ? await getAdminTelemetry(controller.signal) : null;

        if (disposed) {
          return;
        }

        setHealth(healthResponse);
        setSummary({
          ...dashboardResponse,
          feeds: feedResponse.feeds
        });
        setAdminTelemetry(adminResponse);
        setDevices(deviceResponse.devices);
        setTelemetry((current) => mergeTelemetry(current, dashboardResponse.recentTelemetry));

        if (includeTenants) {
          const [tenantResponse, locationsResponse] = await Promise.allSettled([
            getAdminTenants(controller.signal),
            getAdminLocations(controller.signal)
          ]);

          if (!disposed && tenantResponse.status === "fulfilled") {
            setTenants(tenantResponse.value.tenants);
          }
          if (!disposed && locationsResponse.status === "fulfilled") {
            setAdminLocations(locationsResponse.value.locations);
          }
        }

        setApiError(null);
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        if (!disposed) {
          setApiError(error instanceof Error ? error.message : "Unable to reach the backend API");
        }
      } finally {
        snapshotInFlightRef.current = false;
        controller.abort();
      }
    };

    syncSnapshot({ includeTenants: true });

    const fastRefreshId = window.setInterval(() => {
      void syncSnapshot({ includeTenants: false });
    }, 5000);

    const tenantRefreshId = window.setInterval(() => {
      void syncSnapshot({ includeTenants: true });
    }, 30000);

    return () => {
      disposed = true;
      window.clearInterval(fastRefreshId);
      window.clearInterval(tenantRefreshId);
    };
  }, [authSession, isGlobalAdmin]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setUiNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (!authSession || !profileDraft.name.trim() || !profileDraft.tenantName.trim()) {
      setProfileAvailability(null);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      getProfileNameAvailability({
        userName: profileDraft.name,
        tenantName: profileDraft.tenantName
      })
        .then((availability) => setProfileAvailability(availability))
        .catch(() => setProfileAvailability(null));
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [authSession, profileDraft]);

  useEffect(() => {
    if (!showRealtimeNotice) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowRealtimeNotice(false);
    }, 10_000);

    return () => window.clearTimeout(timeoutId);
  }, [showRealtimeNotice]);

  useEffect(() => {
    if (editingFeedKey !== null || feedDraft.tenantId) {
      return;
    }

    const fallbackTenantId = currentTenantId || tenants[0]?.id || summary?.tenant.id || "";
    if (!fallbackTenantId) {
      return;
    }

    setFeedDraft((current) =>
      current.tenantId ? current : { ...current, tenantId: fallbackTenantId }
    );
  }, [currentTenantId, editingFeedKey, feedDraft.tenantId, summary?.tenant.id, tenants]);

  useEffect(() => {
    if (!health?.startedAt) {
      setRuntimeUptimeSeconds(health?.uptimeSeconds ?? 0);
      return;
    }

    const startedAtMs = new Date(health.startedAt).getTime();
    const uptime = Math.max(0, Math.floor((uiNowMs - startedAtMs) / 1000));
    setRuntimeUptimeSeconds(uptime);
  }, [health?.startedAt, health?.uptimeSeconds, uiNowMs]);

  useEffect(() => {
    const tenantId = summary?.tenant.id;
    if (!tenantId) {
      return;
    }

    const socket = io(apiOrigin || "/", {
      auth: { tenantId }
    });

    socket.on("connect", () => setSocketConnected(true));
    socket.on("disconnect", () => setSocketConnected(false));
    socket.on("telemetry:created", (point: TelemetryPoint) => {
      setTelemetry((current) => mergeTelemetry(current, [point]));
    });

    return () => {
      socket.disconnect();
    };
  }, [summary?.tenant.id]);

  useEffect(() => {
    const feeds = summary?.feeds ?? [];
    const latestValues: Record<string, number | null> = {};
    const latestTimestamps: Record<string, string | null> = {};
    const latestDeviceFeedValues: Record<string, number | null> = {};
    const latestDeviceFeedTimestamps: Record<string, string | null> = {};
    const scopedDeviceIds = new Set(liveDeviceIdsForChart);

    for (const feed of feeds) {
      latestValues[feed.key] = null;
      latestTimestamps[feed.key] = null;
    }

    for (const deviceId of scopedDeviceIds) {
      for (const feed of feeds) {
        const key = `${deviceId}|${feed.key}`;
        latestDeviceFeedValues[key] = null;
        latestDeviceFeedTimestamps[key] = null;
      }
    }

    for (const point of liveTelemetry) {
      if (!(point.feedKey in latestValues)) {
        continue;
      }

      if (!latestTimestamps[point.feedKey]) {
        latestTimestamps[point.feedKey] = point.time;
      }

      if (latestValues[point.feedKey] === null) {
        if (typeof point.valueNumeric === "number") {
          latestValues[point.feedKey] = point.valueNumeric;
        } else if (point.valueText) {
          const parsed = Number(point.valueText);
          if (Number.isFinite(parsed)) {
            latestValues[point.feedKey] = parsed;
          }
        }
      }

      const deviceFeedKey = `${point.deviceId}|${point.feedKey}`;
      if (!(deviceFeedKey in latestDeviceFeedValues)) {
        continue;
      }

      if (!latestDeviceFeedTimestamps[deviceFeedKey]) {
        latestDeviceFeedTimestamps[deviceFeedKey] = point.time;
      }

      if (latestDeviceFeedValues[deviceFeedKey] === null) {
        if (typeof point.valueNumeric === "number") {
          latestDeviceFeedValues[deviceFeedKey] = point.valueNumeric;
        } else if (point.valueText) {
          const parsed = Number(point.valueText);
          if (Number.isFinite(parsed)) {
            latestDeviceFeedValues[deviceFeedKey] = parsed;
          }
        }
      }
    }

    latestNumericByFeedRef.current = latestValues;
    latestTimestampByFeedRef.current = latestTimestamps;
    latestNumericByDeviceFeedRef.current = latestDeviceFeedValues;
    latestTimestampByDeviceFeedRef.current = latestDeviceFeedTimestamps;
  }, [liveDeviceIdsForChart, liveTelemetry, summary?.feeds]);

  useEffect(() => {
    const feeds = summary?.feeds ?? [];
    if (feeds.length === 0) {
      setLiveSeries([]);
      return;
    }

    const pushPoint = (nowMs: number) => {
      const point: LiveChartPoint = {
        time: new Date(nowMs).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit"
        }),
        epochMs: nowMs
      };

      for (const feed of feeds) {
        const latestAt = latestTimestampByFeedRef.current[feed.key];
        const stale = latestAt ? nowMs - new Date(latestAt).getTime() > staleAfterMs : true;
        point[feed.key] = stale ? 0 : (latestNumericByFeedRef.current[feed.key] ?? 0);
      }

      lastLiveRenderMsRef.current = nowMs;
      setLiveClockMs(nowMs);
      setLiveSeries((current) => [...current, point].slice(-120));
    };

    if (liveSpeedValue !== "realtime") {
      pushPoint(Date.now());
    }

    const intervalId = window.setInterval(
      () => pushPoint(Date.now()),
      liveSpeedValue === "realtime" ? 1000 : liveSpeedMs
    );

    return () => window.clearInterval(intervalId);
  }, [liveFeedSignature, liveScopeKey, liveSpeedMs, liveSpeedValue, staleAfterMs]);

  useEffect(() => {
    lastLiveRenderMsRef.current = 0;
    setLiveSeries([]);
  }, [liveFeedSignature, liveScopeKey, liveSpeedValue]);

  const liveLatestTelemetryKey = liveTelemetry[0] ? telemetryPointKey(liveTelemetry[0]) : null;

  useEffect(() => {
    if (liveSpeedValue !== "realtime") {
      return;
    }

    const feeds = summary?.feeds ?? [];
    if (feeds.length === 0) {
      return;
    }

    const nowMs = Date.now();
    if (nowMs - lastLiveRenderMsRef.current < liveRefreshMinIntervalMs) {
      return;
    }

    const point: LiveChartPoint = {
      time: new Date(nowMs).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }),
      epochMs: nowMs
    };

    for (const feed of feeds) {
      const latestAt = latestTimestampByFeedRef.current[feed.key];
      const stale = latestAt ? nowMs - new Date(latestAt).getTime() > staleAfterMs : true;
      point[feed.key] = stale ? 0 : (latestNumericByFeedRef.current[feed.key] ?? 0);
    }

    lastLiveRenderMsRef.current = nowMs;
    setLiveClockMs(nowMs);
    setLiveSeries((current) => [...current, point].slice(-120));
  }, [liveFeedSignature, liveLatestTelemetryKey, liveScopeKey, liveSpeedValue, staleAfterMs, summary?.feeds]);

  useEffect(() => {
    if (
      liveScope !== "device" ||
      resolvedLiveDeviceId !== liveAllDevicesOptionValue ||
      !resolvedLiveDeviceFeedKey ||
      liveDeviceIdsForChart.length === 0
    ) {
      setLiveDeviceSeries([]);
      return;
    }

    const pushPoint = (nowMs: number) => {
      const point: LiveChartPoint = {
        time: new Date(nowMs).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit"
        }),
        epochMs: nowMs
      };

      for (const deviceId of liveDeviceIdsForChart) {
        const key = `${deviceId}|${resolvedLiveDeviceFeedKey}`;
        const latestAt = latestTimestampByDeviceFeedRef.current[key];
        const stale = latestAt ? nowMs - new Date(latestAt).getTime() > staleAfterMs : true;
        point[deviceId] = stale ? 0 : (latestNumericByDeviceFeedRef.current[key] ?? 0);
      }

      lastLiveDeviceRenderMsRef.current = nowMs;
      setLiveDeviceSeries((current) => [...current, point].slice(-120));
    };

    if (liveSpeedValue !== "realtime") {
      pushPoint(Date.now());
    }

    const intervalId = window.setInterval(
      () => pushPoint(Date.now()),
      liveSpeedValue === "realtime" ? 1000 : liveSpeedMs
    );

    return () => window.clearInterval(intervalId);
  }, [
    liveDeviceChartKey,
    liveDeviceIdsForChart,
    liveScope,
    liveSpeedMs,
    liveSpeedValue,
    resolvedLiveDeviceFeedKey,
    resolvedLiveDeviceId,
    staleAfterMs
  ]);

  useEffect(() => {
    lastLiveDeviceRenderMsRef.current = 0;
    setLiveDeviceSeries([]);
  }, [liveDeviceChartKey, liveSpeedValue]);

  useEffect(() => {
    if (
      liveSpeedValue !== "realtime" ||
      liveScope !== "device" ||
      resolvedLiveDeviceId !== liveAllDevicesOptionValue ||
      !resolvedLiveDeviceFeedKey ||
      liveDeviceIdsForChart.length === 0
    ) {
      return;
    }

    const nowMs = Date.now();
    if (nowMs - lastLiveDeviceRenderMsRef.current < liveRefreshMinIntervalMs) {
      return;
    }

    const point: LiveChartPoint = {
      time: new Date(nowMs).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }),
      epochMs: nowMs
    };

    for (const deviceId of liveDeviceIdsForChart) {
      const key = `${deviceId}|${resolvedLiveDeviceFeedKey}`;
      const latestAt = latestTimestampByDeviceFeedRef.current[key];
      const stale = latestAt ? nowMs - new Date(latestAt).getTime() > staleAfterMs : true;
      point[deviceId] = stale ? 0 : (latestNumericByDeviceFeedRef.current[key] ?? 0);
    }

    lastLiveDeviceRenderMsRef.current = nowMs;
    setLiveDeviceSeries((current) => [...current, point].slice(-120));
  }, [
    liveLatestTelemetryKey,
    liveSpeedValue,
    liveScope,
    resolvedLiveDeviceId,
    resolvedLiveDeviceFeedKey,
    liveDeviceChartKey,
    liveDeviceIdsForChart,
    staleAfterMs
  ]);

  const apiStatus = useMemo(() => {
    if (health?.ok) {
      return { type: "success" as const, text: "Connected" };
    }

    if (apiError) {
      return { type: "error" as const, text: "Disconnected" };
    }

    return { type: "loading" as const, text: "Checking" };
  }, [apiError, health]);

  const lineData = useMemo(
    () =>
      telemetry
        .filter((point) => point.valueNumeric !== null)
        .slice(0, 40)
        .reverse()
        .map((point) => ({
          time: new Date(point.time).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
          }),
          value: point.valueNumeric ?? 0,
          feedKey: point.feedKey
        })),
    [telemetry]
  );

  const feedBars = useMemo(() => {
    const counts = new Map<string, number>();

    telemetry.forEach((point) => {
      counts.set(point.feedKey, (counts.get(point.feedKey) ?? 0) + 1);
    });

    return (summary?.feeds ?? []).map((feed) => ({
      name: feed.label,
      count: counts.get(feed.key) ?? 0
    }));
  }, [summary?.feeds, telemetry]);

  const latestNumericValue =
    telemetry.find((point) => point.valueNumeric !== null)?.valueNumeric ?? 0;
  const gaugeValue = Math.max(0, Math.min(100, latestNumericValue));

  const page = pageMeta[activeRoute];

  const locationRows = useMemo(() => {
    const telemetryByLocation = new Map<
      string,
      { points: number; latestAt: string | null }
    >();

    telemetry.forEach((point) => {
      const current = telemetryByLocation.get(point.locationId) ?? {
        points: 0,
        latestAt: null
      };

      current.points += 1;
      if (!current.latestAt || new Date(point.time).getTime() > new Date(current.latestAt).getTime()) {
        current.latestAt = point.time;
      }

      telemetryByLocation.set(point.locationId, current);
    });

    return adminLocations.map((location) => {
      const stats = telemetryByLocation.get(location.id);
      return {
        ...location,
        tenantName:
          tenants.find((tenant) => tenant.id === location.tenantId)?.name ?? location.tenantId,
        telemetryPoints: stats?.points ?? 0,
        latestAt: stats?.latestAt ?? null,
        age: formatAge(stats?.latestAt ?? null, uiNowMs)
      };
    });
  }, [adminLocations, telemetry, tenants, uiNowMs]);

  const feedRows = useMemo(() => {
    return (summary?.feeds ?? []).map((feed) => {
      const points = telemetry.filter(
        (item) => item.feedKey === feed.key && item.tenantId === feed.tenantId
      );
      const latest = points[0];
      return {
        ...feed,
        tenantName: tenants.find((tenant) => tenant.id === feed.tenantId)?.name ?? feed.tenantId,
        points: points.length,
        latestValue:
          latest?.valueNumeric !== null && latest?.valueNumeric !== undefined
            ? String(latest.valueNumeric)
            : latest?.valueText ?? "-",
        latestAt: latest?.time ?? null,
        age: formatAge(latest?.time ?? null, uiNowMs)
      };
    });
  }, [summary?.feeds, telemetry, tenants, uiNowMs]);

  const deviceRows = useMemo(() => {
    const telemetryByDevice = new Map<
      string,
      { points: number; latestAt: string | null }
    >();
    const tenantNameById = new Map<string, string>();
    for (const tenant of tenants) {
      tenantNameById.set(tenant.id, tenant.name);
    }
    if (summary?.tenant) {
      tenantNameById.set(summary.tenant.id, summary.tenant.name);
    }

    const locationById = new Map<string, AdminLocation>();
    for (const location of adminLocations) {
      locationById.set(location.id, location);
    }

    telemetry.forEach((point) => {
      const current = telemetryByDevice.get(point.deviceId) ?? {
        points: 0,
        latestAt: null
      };
      current.points += 1;
      if (!current.latestAt || new Date(point.time).getTime() > new Date(current.latestAt).getTime()) {
        current.latestAt = point.time;
      }
      telemetryByDevice.set(point.deviceId, current);
    });

    return devices.map((device) => {
      const stats = telemetryByDevice.get(device.id);
      const location = locationById.get(device.locationId);
      return {
        ...device,
        tenantName: tenantNameById.get(device.tenantId) ?? device.tenantId,
        locationName: location
          ? `${location.building} / ${location.floor} / ${location.room}`
          : device.locationId,
        telemetryPoints: stats?.points ?? 0,
        latestAt: stats?.latestAt ?? null,
        age: formatAge(stats?.latestAt ?? null, uiNowMs)
      };
    });
  }, [adminLocations, devices, summary?.tenant, telemetry, tenants, uiNowMs]);

  const liveSnapshotBars = useMemo(() => {
    return (summary?.feeds ?? []).map((feed) => ({
      name: feed.label,
      value: (() => {
        const latestAt = latestTimestampByFeedRef.current[feed.key];
        const stale = latestAt ? liveClockMs - new Date(latestAt).getTime() > staleAfterMs : true;
        if (stale) {
          return 0;
        }
        return latestNumericByFeedRef.current[feed.key] ?? 0;
      })()
    }));
  }, [liveClockMs, liveSeries.length, staleAfterMs, summary?.feeds]);

  const liveFeedRows = useMemo(() => {
    return (summary?.feeds ?? []).map((feed) => {
      const latestValue = latestNumericByFeedRef.current[feed.key];
      const latestAt = latestTimestampByFeedRef.current[feed.key];
      const ageLabel = formatAge(latestAt, liveClockMs);
      const stale = latestAt ? liveClockMs - new Date(latestAt).getTime() > staleAfterMs : true;

      return {
        key: feed.key,
        label: feed.label,
        kind: feed.kind,
        latestValue: stale ? "0.00" : typeof latestValue === "number" ? latestValue.toFixed(2) : "0.00",
        latestAt,
        ageLabel,
        status: stale ? "Zeroed" : "Live"
      };
    });
  }, [liveClockMs, liveSeries.length, staleAfterMs, summary?.feeds]);

  const liveDeviceComparisonRows = useMemo(() => {
    if (!resolvedLiveDeviceFeedKey) {
      return [];
    }

    return liveDeviceIdsForChart.map((deviceId) => {
      const key = `${deviceId}|${resolvedLiveDeviceFeedKey}`;
      const latestAt = latestTimestampByDeviceFeedRef.current[key];
      const stale = latestAt ? liveClockMs - new Date(latestAt).getTime() > staleAfterMs : true;
      const numericValue = latestNumericByDeviceFeedRef.current[key];

      return {
        deviceId,
        deviceLabel: deviceLabelById.get(deviceId) ?? deviceId,
        value: stale ? "0.00" : typeof numericValue === "number" ? numericValue.toFixed(2) : "0.00",
        latestAt,
        age: formatAge(latestAt ?? null, liveClockMs),
        status: stale ? "Zeroed" : "Live"
      };
    });
  }, [
    deviceLabelById,
    liveClockMs,
    liveDeviceIdsForChart,
    resolvedLiveDeviceFeedKey,
    staleAfterMs
  ]);

  const handleSaveFeed = async () => {
    setSavingFeed(true);
    try {
      const feedToSave =
        !isGlobalAdmin && currentTenantId ? { ...feedDraft, tenantId: currentTenantId } : feedDraft;
      const response = editingFeedKey
        ? await updateFeed(editingFeedKey, feedToSave)
        : await createFeed(feedToSave);

      const [dashboardResponse, tenantResponse, feedResponse] = await Promise.all([
        getDashboardSummary(),
        getAdminTenants(),
        getFeeds()
      ]);
      setSummary({
        ...dashboardResponse,
        feeds: feedResponse.feeds
      });
      setTelemetry((current) => mergeTelemetry(current, dashboardResponse.recentTelemetry));
      setTenants(tenantResponse.tenants);
      setApiError(null);
      setFeedDraft(buildDefaultFeed(response.feed.tenantId));
      setEditingFeedKey(null);
    } catch (error: unknown) {
      setApiError(error instanceof Error ? error.message : "Unable to save feed.");
    } finally {
      setSavingFeed(false);
    }
  };

  const handleEditFeed = (feed: Feed) => {
    setFeedDraft(feed);
    setEditingFeedKey(feed.key);
  };

  const handleCancelFeedEdit = () => {
    setFeedDraft(buildDefaultFeed(feedDraft.tenantId));
    setEditingFeedKey(null);
  };

  const handleDeleteFeed = async (feedKey: string) => {
    await deleteFeed(feedKey);
    setSummary((current) =>
      current
        ? {
            ...current,
            feeds: current.feeds.filter((feed) => feed.key !== feedKey)
          }
        : current
    );
    setTelemetry((current) => current.filter((point) => point.feedKey !== feedKey));
    if (editingFeedKey === feedKey) {
      setFeedDraft(buildDefaultFeed(feedDraft.tenantId));
      setEditingFeedKey(null);
    }
  };

  const refreshAdminTenantsAndLocations = async () => {
    const [tenantResponse, locationResponse] = await Promise.all([
      getAdminTenants(),
      getAdminLocations()
    ]);
    setTenants(tenantResponse.tenants);
    setAdminLocations(locationResponse.locations);
  };

  const handleUpdateTenant = async (
    tenantId: string,
    input: {
      name: string;
      tier: string;
      ingestionLimitPerMinute: number;
      assignedLocationIds: string[];
    }
  ) => {
    await updateTenant(tenantId, input);
    await refreshAdminTenantsAndLocations();
  };

  const handleProvisionTenant = async (tenantId: string) => {
    await provisionTenant(tenantId);
    await refreshAdminTenantsAndLocations();
  };

  const handleSuspendTenant = async (tenantId: string) => {
    await suspendTenant(tenantId);
    await refreshAdminTenantsAndLocations();
  };

  const handleDeleteTenant = async (tenantId: string) => {
    await deleteTenant(tenantId);
    await refreshAdminTenantsAndLocations();
  };

  const handleAddLocation = async (input: {
    building: string;
    floor: string;
    room: string;
    tenantId: string;
  }) => {
    const response = await createLocation(input);
    await refreshAdminTenantsAndLocations();
    return response.location;
  };

  const handleUpdateLocation = async (
    locationId: string,
    input: {
      building: string;
      floor: string;
      room: string;
      tenantId: string;
    }
  ) => {
    await updateLocation(locationId, input);
    await refreshAdminTenantsAndLocations();
  };

  const handleDeleteLocation = async (locationId: string) => {
    await deleteLocation(locationId);
    await refreshAdminTenantsAndLocations();
  };

  const handleAssignDeviceLocation = async (deviceId: string, locationId: string) => {
    const response = await updateDeviceLocation(deviceId, locationId);
    setDevices((current) =>
      current.map((device) => (device.id === deviceId ? response.device : device))
    );
    setApiError(null);
  };

  const handleSavePreferences = async () => {
    setSavingPreferences(true);
    try {
      const preferencesToSave =
        !isGlobalAdmin && preferenceDraft.defaultDashboard === "admin"
          ? { ...preferenceDraft, defaultDashboard: "platform" as const }
          : preferenceDraft;
      const response = await updateUserPreferences(preferencesToSave);
      setAuthSession((current) =>
        current ? { ...current, user: response.user } : current
      );
      setMode(resolveVisualMode(response.user.preferences.mode));
      setDensity(response.user.preferences.density);
      setShowRealtimeNotice(response.user.preferences.realtimeNotice);
      setApiError(null);
    } catch (error: unknown) {
      setApiError(error instanceof Error ? error.message : "Unable to save preferences.");
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await updateUserProfile({
        name: profileDraft.name.trim(),
        tenantName: profileDraft.tenantName.trim()
      });
      setAuthSession((current) =>
        current ? { ...current, user: response.user, tenant: response.tenant } : current
      );
      setTenants((current) =>
        current.map((tenant) => (tenant.id === response.tenant.id ? response.tenant : tenant))
      );
      setSummary((current) =>
        current ? { ...current, tenant: response.tenant } : current
      );
      setProfileDraft({ name: response.user.name, tenantName: response.tenant.name });
      setProfileAvailability(null);
      setApiError(null);
    } catch (error: unknown) {
      setApiError(error instanceof Error ? error.message : "Unable to save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setAuthSession(null);
    setSummary(null);
    setAdminTelemetry(null);
    setTenants([]);
    setAdminLocations([]);
    setDevices([]);
    setTelemetry([]);
    setProfileDraft({ name: "", tenantName: "" });
    setProfileAvailability(null);
    googleButtonRenderedRef.current = false;
  };

  const routeContent = (() => {
    if (activeRoute === "/settings") {
      return (
        <UserSettingsPage
          preferences={preferenceDraft}
          isGlobalAdmin={isGlobalAdmin}
          saving={savingPreferences}
          onChange={setPreferenceDraft}
          onSave={handleSavePreferences}
        />
      );
    }

    if (activeRoute === "/help") {
      return <UserHelpPage apiOrigin={apiOrigin || window.location.origin} />;
    }

    if (activeRoute === "/account") {
      return (
        <AccountPage
          session={authSession}
          tenant={authSession?.tenant ?? summary?.tenant ?? null}
          profileDraft={profileDraft}
          availability={profileAvailability}
          savingProfile={savingProfile}
          onProfileChange={setProfileDraft}
          onSaveProfile={handleSaveProfile}
          onSignOut={handleSignOut}
        />
      );
    }

    if (activeRoute === "/live") {
      return (
        <SpaceBetween size="l">
          <Container
            header={
              <Header
                variant="h2"
                description={
                  isGlobalAdmin
                    ? "Switch between platform-wide telemetry, a specific tenant, a single device, or all devices."
                    : "Switch between your workspace telemetry, a single device, or all devices."
                }
              >
                Live scope
              </Header>
            }
          >
            <SpaceBetween size="m">
              <SegmentedControl
                selectedId={liveScope}
                options={liveScopeOptions}
                label="Live telemetry scope"
                onChange={({ detail }) => setLiveScope(detail.selectedId as LiveScope)}
              />
              <FormField label="Update speed">
                <Select
                  selectedOption={selectedLiveSpeedOption}
                  options={liveSpeedOptions}
                  onChange={({ detail }) =>
                    setLiveSpeedValue(detail.selectedOption.value ?? "realtime")
                  }
                />
              </FormField>
              {liveScope === "tenant" ? (
                <FormField label="Tenant">
                  <Select
                    selectedOption={selectedTenantOption}
                    options={tenantFilterOptions}
                    placeholder="Choose a tenant"
                    onChange={({ detail }) => setLiveTenantId(detail.selectedOption.value ?? null)}
                  />
                </FormField>
              ) : null}
              {liveScope === "device" ? (
                <FormField label="Device">
                  <Select
                    selectedOption={selectedDeviceOption}
                    options={deviceScopeOptions}
                    placeholder="Choose a device"
                    onChange={({ detail }) => setLiveDeviceId(detail.selectedOption.value ?? null)}
                  />
                </FormField>
              ) : null}
              {liveScope === "device" && resolvedLiveDeviceId === liveAllDevicesOptionValue ? (
                <FormField label="Device comparison feed">
                  <Select
                    selectedOption={selectedLiveDeviceFeedOption}
                    options={liveFeedOptions}
                    placeholder="Choose a feed"
                    onChange={({ detail }) => setLiveDeviceFeedKey(detail.selectedOption.value ?? null)}
                  />
                </FormField>
              ) : null}
            </SpaceBetween>
          </Container>

          <Container
            header={
              <Header
                variant="h2"
                description={`${liveScopeDescription}. Update cadence: ${selectedLiveSpeedOption.label}.`}
              >
                Live feed trend
              </Header>
            }
          >
            <div className="chart-frame">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={liveSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" minTickGap={24} />
                  <YAxis width={56} />
                  <Tooltip />
                  {(summary?.feeds ?? []).map((feed, index) => (
                    <Line
                      key={feed.key}
                      type="monotone"
                      dataKey={feed.key}
                      name={feed.label}
                      connectNulls
                      stroke={liveLineColors[index % liveLineColors.length]}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Container>

          {liveScope === "device" && resolvedLiveDeviceId === liveAllDevicesOptionValue ? (
            <ColumnLayout columns={2}>
              <Container
                header={
                  <Header
                    variant="h2"
                    description={`Comparing ${liveDeviceIdsForChart.length} devices on ${selectedLiveDeviceFeedOption?.label ?? "selected feed"}.`}
                  >
                    Device comparison trend
                  </Header>
                }
              >
                <div className="chart-frame">
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={liveDeviceSeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" minTickGap={24} />
                      <YAxis width={56} />
                      <Tooltip />
                      {liveDeviceIdsForChart.map((deviceId, index) => (
                        <Line
                          key={deviceId}
                          type="monotone"
                          dataKey={deviceId}
                          name={deviceLabelById.get(deviceId) ?? deviceId}
                          connectNulls
                          stroke={liveLineColors[index % liveLineColors.length]}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Container>

              <Container
                header={
                  <Header variant="h2" description="All devices shown together with current value and staleness.">
                    Device comparison state
                  </Header>
                }
              >
                <Table
                  columnDefinitions={[
                    { id: "device", header: "Device", cell: (item) => item.deviceLabel },
                    { id: "value", header: "Current value", cell: (item) => item.value },
                    { id: "updated", header: "Last update", cell: (item) => formatTimestamp(item.latestAt) },
                    { id: "age", header: "Age", cell: (item) => item.age },
                    {
                      id: "status",
                      header: "Mode",
                      cell: (item) => (
                        <StatusIndicator type={item.status === "Live" ? "success" : "warning"}>
                          {item.status}
                        </StatusIndicator>
                      )
                    }
                  ]}
                  items={liveDeviceComparisonRows}
                  trackBy="deviceId"
                  empty={<Box textAlign="center">No device telemetry yet</Box>}
                />
              </Container>
            </ColumnLayout>
          ) : null}

          <ColumnLayout columns={2}>
            <Container
              header={
                <Header variant="h2" description="Current held value for each feed.">
                  Live snapshot
                </Header>
              }
            >
              <div className="chart-frame">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={liveSnapshotBars}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis width={48} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2ea597" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Container>

            <Container
              header={
                <Header variant="h2" description="When updates pause, stale feed values automatically fall to zero.">
                  Feed stream state
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  { id: "label", header: "Feed", cell: (item) => item.label },
                  { id: "kind", header: "Chart", cell: (item) => item.kind },
                  { id: "value", header: "Current value", cell: (item) => item.latestValue },
                  { id: "updated", header: "Last update", cell: (item) => formatTimestamp(item.latestAt) },
                  { id: "age", header: "Age", cell: (item) => item.ageLabel },
                  {
                    id: "status",
                    header: "Mode",
                    cell: (item) => (
                      <StatusIndicator type={item.status === "Live" ? "success" : "warning"}>
                        {item.status}
                      </StatusIndicator>
                    )
                  }
                ]}
                items={liveFeedRows}
                trackBy="key"
                empty={<Box textAlign="center">No feeds configured</Box>}
              />
            </Container>
          </ColumnLayout>
        </SpaceBetween>
      );
    }

    if (activeRoute === "/admin" && !isGlobalAdmin) {
      return (
        <Container header={<Header variant="h2">Tenant workspace</Header>}>
          <StatusIndicator type="warning">
            Global admin is not available for this tenant user.
          </StatusIndicator>
        </Container>
      );
    }

    if (activeRoute === "/admin") {
      return (
        <SpaceBetween size="l">
          <Container
            header={
              <Header variant="h2" description="System-wide connection and ingestion counters.">
                Platform telemetry
              </Header>
            }
          >
            <ColumnLayout columns={4} variant="text-grid">
              <Metric label="Active MQTT" value={adminTelemetry?.metrics.activeMqttConnections ?? 0} />
              <Metric label="Messages" value={adminTelemetry?.metrics.messagesReceivedTotal ?? 0} />
              <Metric label="Dropped" value={adminTelemetry?.metrics.messagesDroppedTotal ?? 0} />
              <Metric label="Errors" value={adminTelemetry?.metrics.errorsTotal ?? 0} />
            </ColumnLayout>
          </Container>
          <TenantTable
            tenants={tenants}
            locations={adminLocations}
            onUpdateTenant={handleUpdateTenant}
            onProvisionTenant={handleProvisionTenant}
            onSuspendTenant={handleSuspendTenant}
            onDeleteTenant={handleDeleteTenant}
          />
          <Container header={<Header variant="h2">Resource throttling</Header>}>
            <ColumnLayout columns={3} variant="text-grid">
              <Metric
                label="Default location limit"
                value={adminTelemetry?.throttling.defaultLocationLimitPerMinute ?? 0}
              />
              <Metric
                label="Flush interval"
                value={`${adminTelemetry?.throttling.flushIntervalMs ?? 0} ms`}
              />
              <Metric
                label="Batch size"
                value={adminTelemetry?.throttling.flushBatchSize ?? 0}
              />
            </ColumnLayout>
          </Container>
        </SpaceBetween>
      );
    }

    if (activeRoute === "/locations") {
      return (
        <SpaceBetween size="l">
          <LocationManager
            locations={adminLocations}
            tenants={tenants}
            currentTenantId={currentTenantId}
            isGlobalAdmin={isGlobalAdmin}
            onAddLocation={handleAddLocation}
            onUpdateLocation={handleUpdateLocation}
            onDeleteLocation={handleDeleteLocation}
          />
          <Table
            header={<Header variant="h2">Location activity</Header>}
            columnDefinitions={[
              ...(isGlobalAdmin
                ? [
                    {
                      id: "tenant",
                      header: "Tenant",
                      cell: (item: (typeof locationRows)[number]) => item.tenantName
                    }
                  ]
                : []),
              { id: "building", header: "Building", cell: (item) => item.building },
              { id: "floor", header: "Floor", cell: (item) => item.floor },
              { id: "room", header: "Room", cell: (item) => item.room },
              {
                id: "points",
                header: "Telemetry points",
                cell: (item) => item.telemetryPoints.toLocaleString()
              },
              {
                id: "latest",
                header: "Latest update",
                cell: (item) => formatTimestamp(item.latestAt)
              },
              {
                id: "age",
                header: "Age",
                cell: (item) => item.age
              }
            ]}
            items={locationRows}
            trackBy="id"
            empty={<Box textAlign="center">No locations configured</Box>}
          />
        </SpaceBetween>
      );
    }

    if (activeRoute === "/feeds") {
      return (
        <SpaceBetween size="l">
          <FeedController
            feeds={summary?.feeds ?? []}
            tenants={tenants}
            currentTenantId={currentTenantId}
            isGlobalAdmin={isGlobalAdmin}
            feedDraft={feedDraft}
            setFeedDraft={setFeedDraft}
            editingFeedKey={editingFeedKey}
            savingFeed={savingFeed}
            onSaveFeed={handleSaveFeed}
            onEditFeed={handleEditFeed}
            onCancelEdit={handleCancelFeedEdit}
            onDeleteFeed={handleDeleteFeed}
            onClearFeed={async (feedKey) => {
              await clearFeedHistory(feedKey);
              setTelemetry((current) => current.filter((point) => point.feedKey !== feedKey));
            }}
          />
          <Table
            header={<Header variant="h2">Feed activity</Header>}
            columnDefinitions={[
              { id: "label", header: "Feed", cell: (item) => item.label },
              ...(isGlobalAdmin
                ? [
                    {
                      id: "tenant",
                      header: "Tenant",
                      cell: (item: (typeof feedRows)[number]) => item.tenantName
                    }
                  ]
                : []),
              { id: "key", header: "Key", cell: (item) => item.key },
              { id: "kind", header: "Chart", cell: (item) => item.kind },
              { id: "points", header: "Points", cell: (item) => item.points.toLocaleString() },
              { id: "latestValue", header: "Latest value", cell: (item) => item.latestValue },
              { id: "latestAt", header: "Latest update", cell: (item) => formatTimestamp(item.latestAt) },
              { id: "age", header: "Age", cell: (item) => item.age }
            ]}
            items={feedRows}
            trackBy={(item) => `${item.tenantId}:${item.key}`}
            empty={<Box textAlign="center">No feed activity yet</Box>}
          />
        </SpaceBetween>
      );
    }

    if (activeRoute === "/devices") {
      return (
        <SpaceBetween size="l">
          <DeviceManager
            devices={deviceRows}
            locations={adminLocations}
            tenants={tenants}
            isGlobalAdmin={isGlobalAdmin}
            onAssignLocation={handleAssignDeviceLocation}
          />
        </SpaceBetween>
      );
    }

    return (
      <SpaceBetween size="l">
        <MetricStrip
          title={isGlobalAdmin ? "Live platform status" : "Tenant live status"}
          connectionLabel={isGlobalAdmin ? "MQTT connections" : "Active devices"}
          metrics={adminTelemetry?.metrics ?? summary?.metrics}
          apiStatus={apiStatus.text}
          socketConnected={socketConnected}
          uptimeSeconds={runtimeUptimeSeconds}
        />
        <VisualizationGrid
          lineData={lineData}
          feedBars={feedBars}
          gaugeValue={gaugeValue}
        />
        <LocationHierarchy locations={summary?.locations ?? []} />
      </SpaceBetween>
    );
  })();

  if (authLoading && !authSession) {
    return (
      <LoadingShell
        title="Preparing your workspace"
        description="Checking the active user session."
      />
    );
  }

  if (!authSession) {
    return (
      <SignInPage
        googleClientId={googleClientId}
        isProduction={isProductionBuild}
        googleButtonRef={googleButtonRef}
        error={authError}
      />
    );
  }

  return (
    <div className="app-root">
      <TopNavigation
        identity={{
          href: routeToHref("/platform"),
          title: "IoT Monitoring"
        }}
        utilities={[
          {
            type: "button",
            text: density === "compact" ? "Comfortable" : "Compact",
            onClick: () =>
              setDensity((current) => (current === "compact" ? "comfortable" : "compact"))
          },
          {
            type: "button",
            text: mode === "dark" ? "Light mode" : "Dark mode",
            onClick: () => setMode((current) => (current === "dark" ? "light" : "dark"))
          },
          {
            type: "menu-dropdown",
            text: authSession.user.name,
            description: authSession.user.email,
            iconUrl: authSession.user.avatarUrl,
            iconAlt: `${authSession.user.name} avatar`,
            items: [
              { id: "account", text: "Account" },
              { id: "settings", text: "User settings" },
              { id: "help", text: "Help" },
              { id: "signout", text: "Sign out" }
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === "settings") {
                window.location.hash = routeToHref("/settings");
                return;
              }
              if (detail.id === "help") {
                window.location.hash = routeToHref("/help");
                return;
              }
              if (detail.id === "account") {
                window.location.hash = routeToHref("/account");
                return;
              }
              if (detail.id === "signout") {
                void handleSignOut();
              }
            }
          }
        ]}
      />

      {apiError || showRealtimeNotice ? (
        <div className="floating-flashbar">
          <Flashbar
            items={[
              ...(apiError
                ? [
                    {
                      type: "error" as const,
                      content: apiError,
                      dismissible: false
                    }
                  ]
                : []),
              ...(showRealtimeNotice
                ? [
                    {
                      type: "info" as const,
                      content:
                        "Realtime updates are delivered through Socket.io when backend telemetry arrives.",
                      dismissible: true,
                      onDismiss: () => setShowRealtimeNotice(false)
                    }
                  ]
                : [])
            ]}
          />
        </div>
      ) : null}

      <AppLayout
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              { text: "IoT Monitoring", href: routeToHref("/platform") },
              { text: page.breadcrumb, href: routeToHref(activeRoute) }
            ]}
          />
        }
        navigation={
          <SideNavigation
            activeHref={routeToHref(activeRoute)}
            header={{ href: routeToHref("/platform"), text: "Workspace" }}
            items={[
              {
                type: "section",
                text: "Dashboards",
                items: [
                  { type: "link", text: "Tenant overview", href: routeToHref("/platform") },
                  { type: "link", text: "Live", href: routeToHref("/live") },
                  ...(isGlobalAdmin
                    ? [{ type: "link" as const, text: "Global admin", href: routeToHref("/admin") }]
                    : [])
                ]
              },
              {
                type: "section",
                text: "Operations",
                items: [
                  { type: "link", text: "Locations", href: routeToHref("/locations") },
                  { type: "link", text: "Feeds", href: routeToHref("/feeds") },
                  { type: "link", text: "Devices", href: routeToHref("/devices") }
                ]
              }
            ]}
          />
        }
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        tools={
          <HelpPanel header={<h2>IoT platform</h2>}>
            <SpaceBetween size="m">
              <Box variant="p">
                Device registration starts at <code>POST /api/v1/register</code>. MQTT telemetry publishes
                to the topic structure returned by that endpoint.
              </Box>
              <Link external href="https://cloudscape.design/components/">
                Cloudscape component documentation
              </Link>
            </SpaceBetween>
          </HelpPanel>
        }
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                description={page.description}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <StatusIndicator type={socketConnected ? "success" : "stopped"}>
                      Socket.io
                    </StatusIndicator>
                    <StatusIndicator type={apiStatus.type}>API {apiStatus.text}</StatusIndicator>
                  </SpaceBetween>
                }
              >
                {page.title}
              </Header>
            }
          >
            {routeContent}
          </ContentLayout>
        }
      />
    </div>
  );
}

function LoadingShell({ title, description }: { title: string; description: string }) {
  return (
    <div className="signin-shell">
      <Container>
        <SpaceBetween size="m" alignItems="center">
          <StatusIndicator type="loading">{title}</StatusIndicator>
          <Box color="text-body-secondary">{description}</Box>
        </SpaceBetween>
      </Container>
    </div>
  );
}

function SignInPage({
  googleClientId,
  isProduction,
  googleButtonRef,
  error
}: {
  googleClientId: string;
  isProduction: boolean;
  googleButtonRef: RefObject<HTMLDivElement | null>;
  error: string | null;
}) {
  return (
    <div className="signin-shell">
      <Container
        header={
          <Header
            variant="h1"
            description="Sign in to open your tenant workspace."
          >
            IoT Monitoring
          </Header>
        }
      >
        <SpaceBetween size="l">
          <ColumnLayout columns={2} variant="text-grid">
            <Metric label="Provider" value="Google" />
            <Metric label="Workspace" value="Tenant account" />
          </ColumnLayout>
          {googleClientId ? (
            <div className="google-signin-button" ref={googleButtonRef} />
          ) : !isProduction ? (
            <StatusIndicator type="loading">
              Starting the local development session.
            </StatusIndicator>
          ) : (
            <StatusIndicator type="warning">
              Google sign-in needs GOOGLE_CLIENT_ID in the frontend and backend app settings.
            </StatusIndicator>
          )}
          {error ? <StatusIndicator type="error">{error}</StatusIndicator> : null}
        </SpaceBetween>
      </Container>
    </div>
  );
}

function UserSettingsPage({
  preferences,
  isGlobalAdmin,
  saving,
  onChange,
  onSave
}: {
  preferences: UserPreferences;
  isGlobalAdmin: boolean;
  saving: boolean;
  onChange: (preferences: UserPreferences) => void;
  onSave: () => Promise<void>;
}) {
  const dashboardOptions = [
    { label: "Tenant overview", value: "platform" },
    { label: "Live", value: "live" },
    ...(isGlobalAdmin ? [{ label: "Global admin", value: "admin" }] : [])
  ];
  const defaultDashboardOption =
    dashboardOptions.find((option) => option.value === preferences.defaultDashboard) ??
    dashboardOptions[0];

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button variant="primary" loading={saving} onClick={onSave}>
                Save preferences
              </Button>
            }
          >
            Preferences
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Theme">
            <SegmentedControl
              selectedId={preferences.mode}
              options={[
                { id: "dark", text: "Dark" },
                { id: "light", text: "Light" },
                { id: "system", text: "System" }
              ]}
              onChange={({ detail }) =>
                onChange({ ...preferences, mode: detail.selectedId as UserPreferences["mode"] })
              }
            />
          </FormField>
          <FormField label="Density">
            <SegmentedControl
              selectedId={preferences.density}
              options={[
                { id: "comfortable", text: "Comfortable" },
                { id: "compact", text: "Compact" }
              ]}
              onChange={({ detail }) =>
                onChange({
                  ...preferences,
                  density: detail.selectedId as UserPreferences["density"]
                })
              }
            />
          </FormField>
          <FormField label="Default dashboard">
            <Select
              selectedOption={defaultDashboardOption}
              options={dashboardOptions}
              onChange={({ detail }) =>
                onChange({
                  ...preferences,
                  defaultDashboard:
                    (detail.selectedOption.value as UserPreferences["defaultDashboard"]) ??
                    "platform"
                })
              }
            />
          </FormField>
          <FormField label="Telemetry refresh seconds">
            <Input
              type="number"
              value={String(preferences.telemetryRefreshSeconds)}
              onChange={({ detail }) =>
                onChange({
                  ...preferences,
                  telemetryRefreshSeconds: Math.max(1, Number(detail.value) || 1)
                })
              }
            />
          </FormField>
          <FormField label="Realtime notice">
            <SegmentedControl
              selectedId={preferences.realtimeNotice ? "on" : "off"}
              options={[
                { id: "on", text: "On" },
                { id: "off", text: "Off" }
              ]}
              onChange={({ detail }) =>
                onChange({ ...preferences, realtimeNotice: detail.selectedId === "on" })
              }
            />
          </FormField>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}

function UserHelpPage({ apiOrigin }: { apiOrigin: string }) {
  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Getting started</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <Metric label="Backend API" value={apiOrigin} />
          <Metric label="Register devices" value="/api/v1/register" />
          <Metric label="Send telemetry" value="/api/v1/telemetry" />
        </ColumnLayout>
      </Container>
      <Container header={<Header variant="h2">Account support</Header>}>
        <SpaceBetween size="s">
          <Box>
            Google is the active sign-in provider. Additional providers can be added through the
            same account provider model.
          </Box>
          <Box>
            Device ingestion remains public for hardware onboarding, while dashboard sessions map
            each signed-in user to a tenant workspace.
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}

function AccountPage({
  session,
  tenant,
  profileDraft,
  availability,
  savingProfile,
  onProfileChange,
  onSaveProfile,
  onSignOut
}: {
  session: AuthSession | null;
  tenant: Tenant | null;
  profileDraft: { name: string; tenantName: string };
  availability: ProfileNameAvailability | null;
  savingProfile: boolean;
  onProfileChange: (draft: { name: string; tenantName: string }) => void;
  onSaveProfile: () => Promise<void>;
  onSignOut: () => Promise<void>;
}) {
  if (!session) {
    return <StatusIndicator type="loading">Loading account</StatusIndicator>;
  }

  const nameStatus = (status: NameAvailability | undefined, value: string) => {
    if (value.trim().length < 2) {
      return <StatusIndicator type="info">Use at least 2 characters.</StatusIndicator>;
    }
    if (!status) {
      return <StatusIndicator type="loading">Checking availability</StatusIndicator>;
    }
    return (
      <StatusIndicator type={status.available ? "success" : "error"}>
        {status.available ? "Name is available." : status.reason}
      </StatusIndicator>
    );
  };

  const profileSaveDisabled =
    profileDraft.name.trim().length < 2 ||
    profileDraft.tenantName.trim().length < 2 ||
    availability?.userName.available === false ||
    availability?.tenantName.available === false;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={<Button onClick={() => void onSignOut()}>Sign out</Button>}
          >
            Signed-in user
          </Header>
        }
      >
        <ColumnLayout columns={3} variant="text-grid">
          <Metric label="Name" value={session.user.name} />
          <Metric label="Email" value={session.user.email} />
          <Metric
            label="Workspace mode"
            value={session.user.role === "global-admin" ? "Global admin" : "Tenant workspace"}
          />
        </ColumnLayout>
      </Container>
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button
                variant="primary"
                loading={savingProfile}
                disabled={profileSaveDisabled}
                onClick={() => void onSaveProfile()}
              >
                Save names
              </Button>
            }
          >
            Profile names
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Your display name">
            <SpaceBetween size="xs">
              <Input
                value={profileDraft.name}
                onChange={({ detail }) =>
                  onProfileChange({ ...profileDraft, name: detail.value })
                }
              />
              {nameStatus(availability?.userName, profileDraft.name)}
            </SpaceBetween>
          </FormField>
          <FormField label="Tenant workspace name">
            <SpaceBetween size="xs">
              <Input
                value={profileDraft.tenantName}
                onChange={({ detail }) =>
                  onProfileChange({ ...profileDraft, tenantName: detail.value })
                }
              />
              {nameStatus(availability?.tenantName, profileDraft.tenantName)}
            </SpaceBetween>
          </FormField>
        </SpaceBetween>
      </Container>
      <Container header={<Header variant="h2">Tenant workspace</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <Metric label="Tenant" value={tenant?.name ?? session.user.tenantId} />
          <Metric label="Status" value={tenant?.status ?? "active"} />
          <Metric label="Provider" value={session.user.provider} />
        </ColumnLayout>
      </Container>
    </SpaceBetween>
  );
}

function MetricStrip({
  title,
  connectionLabel,
  metrics,
  apiStatus,
  socketConnected,
  uptimeSeconds
}: {
  title: string;
  connectionLabel: string;
  metrics: DashboardSummary["metrics"] | AdminTelemetry["metrics"] | undefined;
  apiStatus: string;
  socketConnected: boolean;
  uptimeSeconds: number;
}) {
  return (
    <Container header={<Header variant="h2">{title}</Header>}>
      <ColumnLayout columns={4} variant="text-grid">
        <Metric label="API" value={apiStatus} />
        <Metric label="Realtime" value={socketConnected ? "Connected" : "Waiting"} />
        <Metric label={connectionLabel} value={metrics?.activeMqttConnections ?? 0} />
        <Metric label="Uptime" value={`${uptimeSeconds}s`} />
      </ColumnLayout>
    </Container>
  );
}

function VisualizationGrid({
  lineData,
  feedBars,
  gaugeValue
}: {
  lineData: Array<{ time: string; value: number; feedKey: string }>;
  feedBars: Array<{ name: string; count: number }>;
  gaugeValue: number;
}) {
  return (
    <ColumnLayout columns={3}>
      <Container header={<Header variant="h2">Line telemetry</Header>}>
        <div className="chart-frame">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" minTickGap={20} />
              <YAxis width={48} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#0972d3" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Container>
      <Container header={<Header variant="h2">Feed volume</Header>}>
        <div className="chart-frame">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={feedBars}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} width={42} />
              <Tooltip />
              <Bar dataKey="count" fill="#2ea597" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Container>
      <Container header={<Header variant="h2">Latest gauge</Header>}>
        <div className="gauge-frame">
          <ResponsiveContainer width="100%" height={240}>
            <RadialBarChart
              innerRadius="70%"
              outerRadius="100%"
              data={[{ name: "Latest", value: gaugeValue, fill: "#f89256" }]}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar dataKey="value" background />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
          <div className="gauge-value">{Math.round(gaugeValue)}</div>
        </div>
      </Container>
    </ColumnLayout>
  );
}

function LocationHierarchy({ locations }: { locations: DashboardSummary["locations"] }) {
  return (
    <Table
      header={
        <Header variant="h2" description="Feeds grouped by building, floor, and room.">
          Location hierarchy
        </Header>
      }
      columnDefinitions={[
        { id: "building", header: "Building", cell: (item) => item.building },
        { id: "floor", header: "Floor", cell: (item) => item.floor },
        { id: "room", header: "Room", cell: (item) => item.room },
        { id: "status", header: "Status", cell: () => <Badge color="blue">Ready</Badge> }
      ]}
      items={locations}
      trackBy="id"
      empty={<Box textAlign="center">No locations configured</Box>}
    />
  );
}

type LocationDraft = {
  building: string;
  floor: string;
  room: string;
  tenantId: string;
};

function LocationManager({
  locations,
  tenants,
  currentTenantId,
  isGlobalAdmin,
  onAddLocation,
  onUpdateLocation,
  onDeleteLocation
}: {
  locations: AdminLocation[];
  tenants: Tenant[];
  currentTenantId: string;
  isGlobalAdmin: boolean;
  onAddLocation: (input: LocationDraft) => Promise<AdminLocation>;
  onUpdateLocation: (locationId: string, input: LocationDraft) => Promise<void>;
  onDeleteLocation: (locationId: string) => Promise<void>;
}) {
  const [addDraft, setAddDraft] = useState<LocationDraft>({
    building: "",
    floor: "",
    room: "",
    tenantId: currentTenantId || tenants[0]?.id || ""
  });
  const [editDraftByLocationId, setEditDraftByLocationId] = useState<Record<string, LocationDraft>>(
    {}
  );
  const [creatingLocation, setCreatingLocation] = useState(false);
  const [savingLocationId, setSavingLocationId] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  const tenantOptions = useMemo(
    () =>
      tenants.map((tenant) => ({
        label: tenant.name,
        value: tenant.id
      })),
    [tenants]
  );

  const tenantNameById = useMemo(() => {
    const mapping = new Map<string, string>();
    for (const tenant of tenants) {
      mapping.set(tenant.id, tenant.name);
    }
    return mapping;
  }, [tenants]);

  useEffect(() => {
    const fallbackTenantId = currentTenantId || tenantOptions[0]?.value || "";
    if (fallbackTenantId && (!addDraft.tenantId || (!isGlobalAdmin && addDraft.tenantId !== fallbackTenantId))) {
      setAddDraft((current) => ({ ...current, tenantId: fallbackTenantId }));
    }
  }, [addDraft.tenantId, currentTenantId, isGlobalAdmin, tenantOptions]);

  const buildLocationDraft = (location: AdminLocation): LocationDraft => ({
    building: location.building,
    floor: location.floor,
    room: location.room,
    tenantId: location.tenantId
  });

  const getLocationDraft = (location: AdminLocation): LocationDraft =>
    editDraftByLocationId[location.id] ?? buildLocationDraft(location);

  const selectedTenantOption = (tenantId: string) =>
    tenantOptions.find((option) => option.value === tenantId) ?? null;

  const locationTenantId = isGlobalAdmin ? addDraft.tenantId : currentTenantId;
  const canSubmitAddLocation =
    Boolean(addDraft.building.trim()) &&
    Boolean(addDraft.floor.trim()) &&
    Boolean(addDraft.room.trim()) &&
    Boolean(locationTenantId);

  const locationColumns = [
    ...(isGlobalAdmin
      ? [
          {
            id: "tenant",
            header: "Tenant",
            cell: (item: AdminLocation) => tenantNameById.get(item.tenantId) ?? item.tenantId
          }
        ]
      : []),
    { id: "building", header: "Building", cell: (item: AdminLocation) => item.building },
    { id: "floor", header: "Floor", cell: (item: AdminLocation) => item.floor },
    { id: "room", header: "Room", cell: (item: AdminLocation) => item.room },
    {
      id: "actions",
      header: "Actions",
      cell: (item: AdminLocation) => {
        const draft = getLocationDraft(item);
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Popover
              triggerType="custom"
              size="large"
              header="Edit location"
              content={
                <SpaceBetween size="s">
                  <FormField label="Building">
                    <Input
                      value={draft.building}
                      onChange={({ detail }) =>
                        setEditDraftByLocationId((current) => ({
                          ...current,
                          [item.id]: { ...draft, building: detail.value }
                        }))
                      }
                    />
                  </FormField>
                  <FormField label="Floor">
                    <Input
                      value={draft.floor}
                      onChange={({ detail }) =>
                        setEditDraftByLocationId((current) => ({
                          ...current,
                          [item.id]: { ...draft, floor: detail.value }
                        }))
                      }
                    />
                  </FormField>
                  <FormField label="Room">
                    <Input
                      value={draft.room}
                      onChange={({ detail }) =>
                        setEditDraftByLocationId((current) => ({
                          ...current,
                          [item.id]: { ...draft, room: detail.value }
                        }))
                      }
                    />
                  </FormField>
                  {isGlobalAdmin ? (
                    <FormField label="Tenant">
                      <Select
                        selectedOption={selectedTenantOption(draft.tenantId)}
                        options={tenantOptions}
                        onChange={({ detail }) =>
                          setEditDraftByLocationId((current) => ({
                            ...current,
                            [item.id]: {
                              ...draft,
                              tenantId: detail.selectedOption.value ?? draft.tenantId
                            }
                          }))
                        }
                      />
                    </FormField>
                  ) : (
                    <StatusIndicator type="info">
                      This location belongs to your tenant workspace.
                    </StatusIndicator>
                  )}
                  <Button
                    variant="primary"
                    loading={savingLocationId === item.id}
                    disabled={!draft.building || !draft.floor || !draft.room}
                    onClick={async () => {
                      try {
                        setLocationError(null);
                        setSavingLocationId(item.id);
                        await onUpdateLocation(item.id, {
                          ...draft,
                          tenantId: isGlobalAdmin ? draft.tenantId : currentTenantId
                        });
                      } catch (error: unknown) {
                        setLocationError(
                          error instanceof Error ? error.message : "Unable to update location."
                        );
                      } finally {
                        setSavingLocationId(null);
                      }
                    }}
                  >
                    Save changes
                  </Button>
                </SpaceBetween>
              }
            >
              <Button>Edit</Button>
            </Popover>
            <Button
              loading={savingLocationId === item.id}
              onClick={async () => {
                if (!window.confirm(`Delete location '${item.building} / ${item.floor} / ${item.room}'?`)) {
                  return;
                }
                try {
                  setLocationError(null);
                  setSavingLocationId(item.id);
                  await onDeleteLocation(item.id);
                } catch (error: unknown) {
                  setLocationError(
                    error instanceof Error ? error.message : "Unable to delete location."
                  );
                } finally {
                  setSavingLocationId(null);
                }
              }}
            >
              Delete
            </Button>
          </SpaceBetween>
        );
      }
    }
  ];

  return (
    <Table
      header={
        <Header
          variant="h2"
          actions={
            <Popover
              triggerType="custom"
              size="large"
              header="Add location"
              content={
                <SpaceBetween size="s">
                  <FormField label="Building">
                    <Input
                      value={addDraft.building}
                      onChange={({ detail }) =>
                        setAddDraft((current) => ({ ...current, building: detail.value }))
                      }
                    />
                  </FormField>
                  <FormField label="Floor">
                    <Input
                      value={addDraft.floor}
                      onChange={({ detail }) =>
                        setAddDraft((current) => ({ ...current, floor: detail.value }))
                      }
                    />
                  </FormField>
                  <FormField label="Room">
                    <Input
                      value={addDraft.room}
                      onChange={({ detail }) =>
                        setAddDraft((current) => ({ ...current, room: detail.value }))
                      }
                    />
                  </FormField>
                  {isGlobalAdmin ? (
                    <FormField label="Tenant">
                      <Select
                        selectedOption={selectedTenantOption(addDraft.tenantId)}
                        options={tenantOptions}
                        onChange={({ detail }) =>
                          setAddDraft((current) => ({
                            ...current,
                            tenantId: detail.selectedOption.value ?? current.tenantId
                          }))
                        }
                      />
                    </FormField>
                  ) : (
                    <StatusIndicator type="info">
                      New locations are assigned to your tenant workspace.
                    </StatusIndicator>
                  )}
                  <Button
                    variant="primary"
                    loading={creatingLocation}
                    disabled={!canSubmitAddLocation}
                    onClick={async () => {
                      try {
                        setLocationError(null);
                        setCreatingLocation(true);
                        const tenantId = isGlobalAdmin ? addDraft.tenantId : currentTenantId;

                        await onAddLocation({
                          building: addDraft.building.trim(),
                          floor: addDraft.floor.trim(),
                          room: addDraft.room.trim(),
                          tenantId
                        });
                        setAddDraft({
                          building: "",
                          floor: "",
                          room: "",
                          tenantId: currentTenantId || tenantOptions[0]?.value || ""
                        });
                      } catch (error: unknown) {
                        setLocationError(
                          error instanceof Error ? error.message : "Unable to add location."
                        );
                      } finally {
                        setCreatingLocation(false);
                      }
                    }}
                  >
                    Add location
                  </Button>
                </SpaceBetween>
              }
            >
              <Button variant="primary">Add location</Button>
            </Popover>
          }
        >
          Location management
        </Header>
      }
      columnDefinitions={locationColumns}
      items={locations}
      trackBy="id"
      empty={<Box textAlign="center">No locations configured</Box>}
      footer={
        locationError ? <StatusIndicator type="error">{locationError}</StatusIndicator> : undefined
      }
    />
  );
}

type DeviceRow = Device & {
  tenantName: string;
  locationName: string;
  telemetryPoints: number;
  latestAt: string | null;
  age: string;
};

function DeviceManager({
  devices,
  locations,
  tenants,
  isGlobalAdmin,
  onAssignLocation
}: {
  devices: DeviceRow[];
  locations: AdminLocation[];
  tenants: Tenant[];
  isGlobalAdmin: boolean;
  onAssignLocation: (deviceId: string, locationId: string) => Promise<void>;
}) {
  const [editLocationByDeviceId, setEditLocationByDeviceId] = useState<Record<string, string>>({});
  const [savingDeviceId, setSavingDeviceId] = useState<string | null>(null);
  const [deviceError, setDeviceError] = useState<string | null>(null);

  const tenantNameById = useMemo(() => {
    const mapping = new Map<string, string>();
    for (const tenant of tenants) {
      mapping.set(tenant.id, tenant.name);
    }
    return mapping;
  }, [tenants]);

  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        label: `${location.building} / ${location.floor} / ${location.room}`,
        value: location.id,
        description: isGlobalAdmin
          ? `Tenant: ${tenantNameById.get(location.tenantId) ?? location.tenantId}`
          : undefined
      })),
    [isGlobalAdmin, locations, tenantNameById]
  );

  const optionByValue = useMemo(() => {
    const mapping = new Map<string, { label: string; value: string; description?: string }>();
    for (const option of locationOptions) {
      if (option.value) {
        mapping.set(option.value, option);
      }
    }
    return mapping;
  }, [locationOptions]);

  const getDraftLocationId = (device: DeviceRow) =>
    editLocationByDeviceId[device.id] ?? device.locationId;

  const selectedLocationOption = (device: DeviceRow) =>
    optionByValue.get(getDraftLocationId(device)) ?? null;

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Assign each device to a location. Device tenant follows the selected location tenant."
        >
          Device management
        </Header>
      }
    >
      <Table
        columnDefinitions={[
          {
            id: "hardwareId",
            header: "Hardware ID",
            cell: (item: DeviceRow) => item.hardwareId,
            minWidth: 180
          },
          {
            id: "location",
            header: "Location",
            cell: (item: DeviceRow) => item.locationName,
            minWidth: 220
          },
          {
            id: "status",
            header: "Status",
            cell: (item: DeviceRow) => (
              <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
                {item.status}
              </StatusIndicator>
            ),
            width: 120
          },
          {
            id: "latestAt",
            header: "Latest update",
            cell: (item: DeviceRow) => formatTimestamp(item.latestAt),
            minWidth: 190
          },
          {
            id: "actions",
            header: "Actions",
            cell: (item: DeviceRow) => (
              <Popover
                triggerType="custom"
                size="large"
                header="Edit device location"
                content={
                  <SpaceBetween size="s">
                    <FormField label="Location">
                      <Select
                        selectedOption={selectedLocationOption(item)}
                        options={locationOptions}
                        placeholder="Select location"
                        expandToViewport
                        onChange={({ detail }) =>
                          setEditLocationByDeviceId((current) => ({
                            ...current,
                            [item.id]: detail.selectedOption.value ?? getDraftLocationId(item)
                          }))
                        }
                      />
                    </FormField>
                    <Button
                      variant="primary"
                      loading={savingDeviceId === item.id}
                      disabled={!getDraftLocationId(item)}
                      onClick={async () => {
                        try {
                          setDeviceError(null);
                          setSavingDeviceId(item.id);
                          await onAssignLocation(item.id, getDraftLocationId(item));
                        } catch (error: unknown) {
                          setDeviceError(
                            error instanceof Error ? error.message : "Unable to update device location."
                          );
                        } finally {
                          setSavingDeviceId(null);
                        }
                      }}
                    >
                      Save
                    </Button>
                  </SpaceBetween>
                }
              >
                <Button>Edit</Button>
              </Popover>
            ),
            width: 120
          }
        ]}
        resizableColumns
        items={devices}
        trackBy="id"
        empty={<Box textAlign="center">No devices registered yet</Box>}
        footer={deviceError ? <StatusIndicator type="error">{deviceError}</StatusIndicator> : undefined}
      />
    </Container>
  );
}

function FeedController({
  feeds,
  tenants,
  currentTenantId,
  isGlobalAdmin,
  feedDraft,
  setFeedDraft,
  editingFeedKey,
  savingFeed,
  onSaveFeed,
  onEditFeed,
  onCancelEdit,
  onDeleteFeed,
  onClearFeed
}: {
  feeds: Feed[];
  tenants: Tenant[];
  currentTenantId: string;
  isGlobalAdmin: boolean;
  feedDraft: Feed;
  setFeedDraft: (feed: Feed) => void;
  editingFeedKey: string | null;
  savingFeed: boolean;
  onSaveFeed: () => Promise<void>;
  onEditFeed: (feed: Feed) => void;
  onCancelEdit: () => void;
  onDeleteFeed: (feedKey: string) => Promise<void>;
  onClearFeed: (feedKey: string) => Promise<void>;
}) {
  const selectedKind =
    feedKindOptions.find((option) => option.value === feedDraft.kind) ?? feedKindOptions[0];
  const tenantOptions = tenants.map((tenant) => ({
    label: tenant.name,
    value: tenant.id
  }));
  const selectedTenant =
    tenantOptions.find((option) => option.value === feedDraft.tenantId) ?? null;
  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const tenant of tenants) {
      map.set(tenant.id, tenant.name);
    }
    return map;
  }, [tenants]);
  const isEditing = editingFeedKey !== null;

  useEffect(() => {
    if (!isGlobalAdmin && currentTenantId && feedDraft.tenantId !== currentTenantId) {
      setFeedDraft({ ...feedDraft, tenantId: currentTenantId });
    }
  }, [currentTenantId, feedDraft, isGlobalAdmin, setFeedDraft]);

  const feedColumns = [
    { id: "label", header: "Feed", cell: (item: Feed) => item.label },
    ...(isGlobalAdmin
      ? [
          {
            id: "tenant",
            header: "Tenant",
            cell: (item: Feed) => tenantNameById.get(item.tenantId) ?? item.tenantId
          }
        ]
      : []),
    { id: "key", header: "Key", cell: (item: Feed) => item.key },
    { id: "unit", header: "Unit", cell: (item: Feed) => item.unit ?? "-" },
    { id: "kind", header: "Chart", cell: (item: Feed) => item.kind },
    {
      id: "actions",
      header: "Actions",
      cell: (item: Feed) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={() => onEditFeed(item)}>
            Edit
          </Button>
          <Button
            onClick={async () => {
              if (window.confirm(`Delete feed '${item.key}'?`)) {
                await onDeleteFeed(item.key);
              }
            }}
          >
            Delete
          </Button>
          <Button onClick={() => onClearFeed(item.key)} iconName="remove">
            Clear history
          </Button>
        </SpaceBetween>
      )
    }
  ];

  return (
    <Container
      header={
        <Header variant="h2" description="Create, edit, delete, and clear history for any number of feeds.">
          Feed controller
        </Header>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={4}>
          <FormField label="Key">
            <Input
              value={feedDraft.key}
              disabled={isEditing}
              onChange={({ detail }) => setFeedDraft({ ...feedDraft, key: detail.value })}
            />
          </FormField>
          <FormField label="Label">
            <Input
              value={feedDraft.label}
              onChange={({ detail }) => setFeedDraft({ ...feedDraft, label: detail.value })}
            />
          </FormField>
          <FormField label="Unit">
            <Input
              value={feedDraft.unit ?? ""}
              onChange={({ detail }) => setFeedDraft({ ...feedDraft, unit: detail.value })}
            />
          </FormField>
          <FormField label="Visualization">
            <Select
              selectedOption={selectedKind}
              options={feedKindOptions}
              onChange={({ detail }) =>
                setFeedDraft({
                  ...feedDraft,
                  kind: (detail.selectedOption.value ?? "line") as Feed["kind"]
                })
              }
            />
          </FormField>
        </ColumnLayout>
        {isGlobalAdmin ? (
          <FormField label="Tenant">
            <Select
              selectedOption={selectedTenant}
              options={tenantOptions}
              placeholder="Select tenant"
              onChange={({ detail }) =>
                setFeedDraft({
                  ...feedDraft,
                  tenantId: detail.selectedOption.value ?? feedDraft.tenantId
                })
              }
            />
          </FormField>
        ) : (
          <StatusIndicator type="info">
            New feeds are assigned to your tenant workspace.
          </StatusIndicator>
        )}
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            loading={savingFeed}
            disabled={!feedDraft.key || !feedDraft.label || !feedDraft.tenantId}
            onClick={onSaveFeed}
          >
            {isEditing ? "Update feed" : "Create feed"}
          </Button>
          {isEditing ? (
            <Button onClick={onCancelEdit}>
              Cancel edit
            </Button>
          ) : null}
        </SpaceBetween>
        <Table
          columnDefinitions={feedColumns}
          items={feeds}
          trackBy={(item) => `${item.tenantId}:${item.key}`}
          empty={<Box textAlign="center">No feeds configured</Box>}
        />
      </SpaceBetween>
    </Container>
  );
}

type TenantDraft = {
  name: string;
  tier: string;
  ingestionLimitPerMinute: string;
  assignedLocationIds: string[];
};

function TenantTable({
  tenants,
  locations,
  onUpdateTenant,
  onProvisionTenant,
  onSuspendTenant,
  onDeleteTenant
}: {
  tenants: Tenant[];
  locations: AdminLocation[];
  onUpdateTenant: (
    tenantId: string,
    input: {
      name: string;
      tier: string;
      ingestionLimitPerMinute: number;
      assignedLocationIds: string[];
    }
  ) => Promise<void>;
  onProvisionTenant: (tenantId: string) => Promise<void>;
  onSuspendTenant: (tenantId: string) => Promise<void>;
  onDeleteTenant: (tenantId: string) => Promise<void>;
}) {
  const [editDraftByTenantId, setEditDraftByTenantId] = useState<Record<string, TenantDraft>>({});
  const [savingTenantId, setSavingTenantId] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);

  const tenantById = useMemo(() => {
    const map = new Map<string, Tenant>();
    for (const tenant of tenants) {
      map.set(tenant.id, tenant);
    }
    return map;
  }, [tenants]);

  const assignedLocationIdsByTenant = useMemo(() => {
    const mapping = new Map<string, string[]>();
    for (const location of locations) {
      const current = mapping.get(location.tenantId) ?? [];
      current.push(location.id);
      mapping.set(location.tenantId, current);
    }
    return mapping;
  }, [locations]);

  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        label: `${location.building} / ${location.floor} / ${location.room}`,
        value: location.id,
        description: `Assigned to ${tenantById.get(location.tenantId)?.name ?? "Unknown"}`
      })),
    [locations, tenantById]
  );

  const optionByValue = useMemo(() => {
    const mapping = new Map<string, { label: string; value: string; description?: string }>();
    for (const option of locationOptions) {
      if (option.value) {
        mapping.set(option.value, option);
      }
    }
    return mapping;
  }, [locationOptions]);

  const buildTenantDraft = (tenant: Tenant): TenantDraft => ({
    name: tenant.name,
    tier: tenant.tier,
    ingestionLimitPerMinute: String(tenant.ingestionLimitPerMinute),
    assignedLocationIds: assignedLocationIdsByTenant.get(tenant.id) ?? []
  });

  const getTenantDraft = (tenant: Tenant) =>
    editDraftByTenantId[tenant.id] ?? buildTenantDraft(tenant);

  const selectedLocationOptions = (ids: string[]) =>
    ids
      .map((id) => optionByValue.get(id))
      .filter((option): option is { label: string; value: string; description?: string } =>
        Boolean(option)
      );

  const parseLimit = (value: string) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      throw new Error("Ingestion limit must be a positive number.");
    }
    return Math.floor(parsed);
  };

  return (
    <Table
      header={
        <Header variant="h2">
          Tenant management
        </Header>
      }
      columnDefinitions={[
        { id: "name", header: "Tenant", cell: (item: Tenant) => item.name },
        {
          id: "status",
          header: "Status",
          cell: (item: Tenant) => (
            <StatusIndicator type={item.status === "active" ? "success" : "stopped"}>
              {item.status}
            </StatusIndicator>
          )
        },
        { id: "tier", header: "Tier", cell: (item: Tenant) => item.tier },
        {
          id: "locations",
          header: "Locations",
          cell: (item: Tenant) => (assignedLocationIdsByTenant.get(item.id)?.length ?? 0).toString()
        },
        {
          id: "limit",
          header: "Ingestion limit",
          cell: (item: Tenant) => item.ingestionLimitPerMinute.toLocaleString()
        },
        {
          id: "actions",
          header: "Actions",
          cell: (item: Tenant) => {
            const draft = getTenantDraft(item);
            return (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={async () => {
                    try {
                      setTenantError(null);
                      setSavingTenantId(item.id);
                      await onProvisionTenant(item.id);
                    } catch (error: unknown) {
                      setTenantError(
                        error instanceof Error ? error.message : "Unable to provision tenant."
                      );
                    } finally {
                      setSavingTenantId(null);
                    }
                  }}
                  loading={savingTenantId === item.id}
                  iconName="status-positive"
                >
                  Provision
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      setTenantError(null);
                      setSavingTenantId(item.id);
                      await onSuspendTenant(item.id);
                    } catch (error: unknown) {
                      setTenantError(
                        error instanceof Error ? error.message : "Unable to suspend tenant."
                      );
                    } finally {
                      setSavingTenantId(null);
                    }
                  }}
                  loading={savingTenantId === item.id}
                  iconName="status-stopped"
                >
                  Suspend
                </Button>
                <Popover
                  triggerType="custom"
                  size="large"
                  header="Edit tenant"
                  content={
                    <SpaceBetween size="s">
                      <FormField label="Tenant name">
                        <Input
                          value={draft.name}
                          onChange={({ detail }) =>
                            setEditDraftByTenantId((current) => ({
                              ...current,
                              [item.id]: { ...draft, name: detail.value }
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Tier">
                        <Input
                          value={draft.tier}
                          onChange={({ detail }) =>
                            setEditDraftByTenantId((current) => ({
                              ...current,
                              [item.id]: { ...draft, tier: detail.value }
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Ingestion limit per minute">
                        <Input
                          value={draft.ingestionLimitPerMinute}
                          onChange={({ detail }) =>
                            setEditDraftByTenantId((current) => ({
                              ...current,
                              [item.id]: {
                                ...draft,
                                ingestionLimitPerMinute: detail.value
                              }
                            }))
                          }
                        />
                      </FormField>
                      <FormField label="Assigned locations">
                        <Multiselect
                          selectedOptions={selectedLocationOptions(draft.assignedLocationIds)}
                          options={locationOptions}
                          placeholder="Select locations"
                          onChange={({ detail }) =>
                            setEditDraftByTenantId((current) => ({
                              ...current,
                              [item.id]: {
                                ...draft,
                                assignedLocationIds: detail.selectedOptions
                                  .map((option) => option.value)
                                  .filter((value): value is string => Boolean(value))
                              }
                            }))
                          }
                        />
                      </FormField>
                      <Button
                        variant="primary"
                        loading={savingTenantId === item.id}
                        disabled={!draft.name || !draft.tier || !draft.ingestionLimitPerMinute}
                        onClick={async () => {
                          try {
                            setTenantError(null);
                            setSavingTenantId(item.id);
                            await onUpdateTenant(item.id, {
                              name: draft.name,
                              tier: draft.tier,
                              ingestionLimitPerMinute: parseLimit(draft.ingestionLimitPerMinute),
                              assignedLocationIds: draft.assignedLocationIds
                            });
                          } catch (error: unknown) {
                            setTenantError(
                              error instanceof Error ? error.message : "Unable to update tenant."
                            );
                          } finally {
                            setSavingTenantId(null);
                          }
                        }}
                      >
                        Save changes
                      </Button>
                    </SpaceBetween>
                  }
                >
                  <Button>Edit</Button>
                </Popover>
                <Button
                  onClick={async () => {
                    if (!window.confirm(`Delete tenant '${item.name}'?`)) {
                      return;
                    }
                    try {
                      setTenantError(null);
                      setSavingTenantId(item.id);
                      await onDeleteTenant(item.id);
                    } catch (error: unknown) {
                      setTenantError(
                        error instanceof Error ? error.message : "Unable to delete tenant."
                      );
                    } finally {
                      setSavingTenantId(null);
                    }
                  }}
                  loading={savingTenantId === item.id}
                >
                  Delete
                </Button>
              </SpaceBetween>
            );
          }
        }
      ]}
      items={tenants}
      trackBy="id"
      empty={<Box textAlign="center">No tenants</Box>}
      footer={tenantError ? <StatusIndicator type="error">{tenantError}</StatusIndicator> : undefined}
    />
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box fontSize="display-l" fontWeight="bold">
        {value}
      </Box>
    </div>
  );
}
